/* eslint-disable max-lines-per-function, max-statements, no-undef */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  ActionAttentionRing,
  findDefaultAttentionButton,
  isAttentionEligibleButton,
} from './ActionAttentionRing';
import { actionInvocationOrigin, withActionInvocationOrigin } from './actionInvocationOrigin';


interface OverflowAction {
  button: HTMLButtonElement;
  disabled: boolean;
  iconClass: string | null;
  key: string;
  label: string;
  title: string;
}

interface ActionBarActionsProps extends React.PropsWithChildren {
  attentionActive?: boolean;
  className?: string;
  dataActionChoiceMade?: boolean;
}

interface MenuPosition {
  left: number;
  top: number;
  width: number;
}

const ACTION_SELECTOR = '.ai-overview-action-btn, .agent-suggestion-pill';

const actionOwnerUnavailable = (root: HTMLElement | null): boolean => {
  if (!root?.isConnected) return true;
  const bar = root.closest<HTMLElement>('.ai-overview-action-bar');

  return Boolean(bar && (bar.dataset.actionSourceSelected === 'false' || bar.hidden
    || bar.getAttribute('aria-hidden') === 'true'
    || bar.matches('.ai-overview-action-bar--historical, .ai-overview-action-bar--hidden')
    || globalThis.getComputedStyle(bar).display === 'none'));
};

/**
 * Actions that must never be collapsed into the More actions menu. A user who
 * ran out of credits has to see how to pay from the bar itself — burying
 * Add Credits / Upgrade one click deep reads as "there is nothing I can do".
 */
const PINNED_ACTION_SELECTOR = [
  '.ai-overview-action-btn--add-credit',
  '.ai-overview-action-btn--upgrade',
  '[data-action-placement="pinned"]',
].join(', ');

const isPinnedAction = (button: HTMLButtonElement): boolean => button.matches(PINNED_ACTION_SELECTOR);
const isSecondaryAction = (button: HTMLButtonElement): boolean => button.dataset.actionPlacement === 'secondary';

/**
 * How many controls each row should hold once the bar has to wrap, or null
 * while everything still fits on one row.
 *
 * Wrapped rows are laid out as equal columns sized to the widest control, so
 * capacity is measured in those columns rather than in the controls' own
 * widths. Dividing the controls across the rows they need keeps the break
 * even — four controls in a bar that fits three become 2 + 2 rather than
 * 3 + 1, which reads as a stray button.
 */
export const balancedRowSize = (
  itemWidths: number[],
  availableWidth: number,
  gap: number,
): number | null => {
  if (itemWidths.length < 2 || availableWidth <= 0) return null;

  const widest = Math.max(...itemWidths);

  if (widest <= 0) return null;

  // Sub-pixel layout rounding must not cost a column.
  const capacity = Math.max(1, Math.floor((availableWidth + gap + 0.5) / (widest + gap)));

  // Still one row: leave the bar alone so the controls keep natural widths.
  if (capacity >= itemWidths.length) return null;

  const rows = Math.ceil(itemWidths.length / capacity);

  return Math.min(capacity, Math.ceil(itemWidths.length / rows));
};

const actionLabel = (button: HTMLButtonElement): string => (
  button.querySelector<HTMLElement>('.ai-overview-action-btn__label')?.textContent?.trim()
  || button.getAttribute('aria-label')
  || button.textContent?.trim()
  || button.title
  || 'Action'
);

const selectedAction = (buttons: HTMLButtonElement[]): HTMLButtonElement | null => (
  buttons.find((button) => button.dataset.attentionPrimary === 'true')
  || buttons.find((button) => (
    button.getAttribute('aria-pressed') === 'true'
    || button.classList.contains('ai-overview-action-btn--selected')
    || button.classList.contains('ai-overview-action-btn--done')
  ))
  || null
);

const sameOverflow = (left: OverflowAction[], right: OverflowAction[]): boolean => (
  left.length === right.length
  && left.every((item, index) => (
    item.button === right[index]?.button
    && item.disabled === right[index]?.disabled
    && item.iconClass === right[index]?.iconClass
    && item.label === right[index]?.label
    && item.title === right[index]?.title
  ))
);

/**
 * Shared action-bar group. When a bar has more than two actions, the action
 * selected by the attention contract stays visible and every alternative is
 * collected under one neutral More actions control.
 */
export const ActionBarActions: React.FC<ActionBarActionsProps> = ({
  attentionActive = false,
  children,
  className = '',
  dataActionChoiceMade,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerWrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [overflowActions, setOverflowActions] = useState<OverflowAction[]>([]);

  const refreshOverflow = useCallback(() => {
    const root = rootRef.current;

    if (!root) return;

    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>(ACTION_SELECTOR))
      .filter((button) => !button.closest('[data-action-overflow-control="true"]'));

    buttons.forEach((button) => {
      delete button.dataset.actionOverflowHidden;
      delete button.dataset.actionOverflowPrimary;
    });

    if (buttons.length <= 2 && !buttons.some(isSecondaryAction)) {
      setOverflowActions((current) => (current.length === 0 ? current : []));

      return;
    }

    // Credit recovery owns attention, while the workflow action still stays
    // visible beside it instead of moving into the overflow menu.
    const attentionDefault = findDefaultAttentionButton(root, false);
    const primaryCandidates = buttons.filter((button) => !isSecondaryAction(button));
    const primary = selectedAction(primaryCandidates)
      || (attentionDefault && primaryCandidates.includes(attentionDefault) ? attentionDefault : null)
      || primaryCandidates.at(-1)
      || null;

    if (primary) primary.dataset.actionOverflowPrimary = 'true';
    const next = buttons
      .filter((button) => isSecondaryAction(button) || (
        buttons.length > 2 && button !== primary && !isPinnedAction(button)
      ))
      .map((button, index): OverflowAction => {
        button.dataset.actionOverflowHidden = 'true';
        const label = actionLabel(button);

        return {
          button,
          disabled: !isAttentionEligibleButton(button),
          iconClass: button.querySelector<HTMLElement>('i')?.className || null,
          key: button.dataset.actionKey || button.id || `${label}:${index}`,
          label,
          title: button.title || label,
        };
      });

    setOverflowActions((current) => (sameOverflow(current, next) ? current : next));
  }, []);

  /** Measure the fixed controls, then divide wrapped rows evenly. */
  const balanceRows = useCallback(() => {
    const root = rootRef.current;

    if (!root) return;

    // The flex row exposes each fixed control before balanced grid tracks apply.
    delete root.dataset.actionRows;
    const items = Array.from(root.children).filter((child): child is HTMLElement => (
      child instanceof HTMLElement
      && child.dataset.actionOverflowHidden !== 'true'
      && child.dataset.actionLayoutMessage !== 'true'
      && globalThis.getComputedStyle(child).position !== 'absolute'
    ));
    const gap = parseFloat(globalThis.getComputedStyle(root).columnGap) || 0;
    const perRow = balancedRowSize(
      items.map((item) => item.getBoundingClientRect().width),
      root.clientWidth,
      gap,
    );

    if (perRow === null) {
      root.style.removeProperty('--action-row-items');

      return;
    }

    root.style.setProperty('--action-row-items', String(perRow));
    root.dataset.actionRows = 'balanced';
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;

    if (!root) return undefined;

    let cancelled = false;
    const refresh = () => {
      refreshOverflow();
      balanceRows();
    };

    queueMicrotask(() => {
      if (!cancelled) refresh();
    });
    // Row balancing depends on the bar's own width, which changes without any
    // DOM mutation when the artifact panel opens. Absent in jsdom, where the
    // one-shot measurement above is all the tests need.
    const resizeObserver = typeof globalThis.ResizeObserver === 'function'
      ? new globalThis.ResizeObserver(() => balanceRows())
      : null;

    resizeObserver?.observe(root);
    const observer = new MutationObserver(refresh);

    observer.observe(root, {
      attributes: true,
      attributeFilter: [
        'aria-busy',
        'aria-disabled',
        'aria-pressed',
        'class',
        'data-attention-primary',
        'data-action-placement',
        'disabled',
      ],
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      resizeObserver?.disconnect();
      root.querySelectorAll<HTMLElement>('[data-action-overflow-hidden="true"]')
        .forEach((element) => delete element.dataset.actionOverflowHidden);
      root.querySelectorAll<HTMLElement>('[data-action-overflow-primary="true"]')
        .forEach((element) => delete element.dataset.actionOverflowPrimary);
    };
  }, [balanceRows, children, refreshOverflow]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;

      if (!menuRef.current?.contains(target) && !triggerWrapRef.current?.contains(target)) {
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    const bar = rootRef.current?.closest<HTMLElement>('.ai-overview-action-bar');

    if (!bar) return undefined;
    const sourceId = bar.dataset.actionSourceEntryId || bar.dataset.sourceEntryId;
    const closeStaleMenu = () => {
      if (actionOwnerUnavailable(rootRef.current)
        || sourceId !== (bar.dataset.actionSourceEntryId || bar.dataset.sourceEntryId)) setMenuOpen(false);
    };
    const observer = new MutationObserver(closeStaleMenu);

    observer.observe(bar, { attributes: true, attributeFilter: [
      'data-action-source-selected', 'data-action-source-entry-id', 'data-source-entry-id',
      'class', 'style', 'hidden', 'aria-hidden',
    ] });
    closeStaleMenu();

    return () => observer.disconnect();
  }, [menuOpen]);

  const placeMenu = useCallback(() => {
    const trigger = triggerWrapRef.current;

    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportInset = 12;
    const width = Math.min(300, Math.max(210, rect.width));
    const estimatedHeight = Math.min(360, overflowActions.length * 40 + 12);
    const spaceBelow = window.innerHeight - rect.bottom - viewportInset;
    const top = spaceBelow >= estimatedHeight
      ? rect.bottom + 7
      : Math.max(viewportInset, rect.top - estimatedHeight - 7);

    setMenuPosition({
      left: Math.min(
        Math.max(viewportInset, rect.left),
        Math.max(viewportInset, window.innerWidth - width - viewportInset),
      ),
      top,
      width,
    });
  }, [overflowActions.length]);

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;

    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);

    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [menuOpen, placeMenu]);

  return (
    <div
      ref={rootRef}
      className={`ai-overview-action-bar__actions${className}`}
      data-action-choice-made={dataActionChoiceMade || undefined}
      data-action-overflowing={overflowActions.length > 0 || undefined}
    >
      <ActionAttentionRing active={attentionActive} />
      {overflowActions.length > 0 && (
        <div ref={triggerWrapRef} className="ai-overview-more-actions" data-action-overflow-control="true">
          <button
            type="button"
            className="ai-overview-action-btn ai-overview-more-actions__trigger"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => {
              if (!menuOpen) placeMenu();
              setMenuOpen((open) => !open);
            }}
          >
            <span className="ai-overview-action-btn__label">More actions</span>
            <i className="fa-solid fa-chevron-down ai-overview-more-actions__chevron" aria-hidden="true" />
          </button>
          {menuOpen && menuPosition && createPortal(
            <div
              ref={menuRef}
              className="ai-overview-more-actions__menu"
              role="menu"
              aria-label="More actions"
              style={menuPosition}
            >
              {overflowActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="ai-overview-more-actions__item"
                  disabled={action.disabled}
                  role="menuitem"
                  title={action.title}
                  onClick={(event) => {
                    if (!action.disabled && isAttentionEligibleButton(action.button)
                      && !actionOwnerUnavailable(rootRef.current)) withActionInvocationOrigin(
                      actionInvocationOrigin(event.currentTarget), () => action.button.click(),
                    );
                    setMenuOpen(false);
                  }}
                >
                  {action.iconClass && <i className={action.iconClass} aria-hidden="true" />}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>,
            document.body,
          )}
        </div>
      )}
      {children}
    </div>
  );
};
